import { useEffect, useState } from 'react'
import { type MarketScanDetail, getWsUrl } from '../../lib/api.ts'
import { Separator } from '@/components/ui/separator'

function ScanCard({ market }: { market: MarketScanDetail }) {
  const isEntry = market.edge > 0

  return (
    <div className={`scan-card ${isEntry ? 'favorite' : ''}`}>
      {isEntry && (
        <div className="font-mono" style={{ fontSize: '8px', color: 'var(--green)', letterSpacing: '0.1em', marginBottom: '4px', textTransform: 'uppercase' }}>
          ★ Snipe Found
        </div>
      )}
      <div className="scan-card-slug" title={market.crypto}>
        {market.crypto.toUpperCase()} Snipe
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>
        <div>
          <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '2px' }}>STRIKE</div>
          <div className="font-mono" style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 700 }}>
            ${market.strikePrice.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '2px' }}>ORACLE</div>
          <div className="font-mono" style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 700 }}>
            ${market.oraclePrice.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '2px' }}>ASK (¢)</div>
          <div className="font-mono" style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 700 }}>
            {(market.askPrice * 100).toFixed(1)}¢
          </div>
        </div>
        <div>
          <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '2px' }}>EDGE ($)</div>
          <div className="font-mono" style={{ fontSize: '13px', color: market.edge > 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
            ${market.edge.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  )
}

export function MarketScan() {
  const [markets, setMarkets] = useState<MarketScanDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  useEffect(() => {
    // WebSocket real-time updates
    const wsUrl = getWsUrl()
    const ws = new WebSocket(wsUrl)
    
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'subscribe', channels: ['scan'] }))
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'SCAN_UPDATE') {
          // Unconditionally set loading to false first
          setLoading(false)
          
          if (msg.payload && Array.isArray(msg.payload.opportunities)) {
            setMarkets(msg.payload.opportunities)
          } else {
            // Fallback if payload or opportunities is missing/malformed
            setMarkets([])
          }
          
          setLastUpdated(new Date())
        }
      } catch (err) {
        console.error('Failed to parse scan update:', err)
      }
    }

    ws.onerror = (err) => {
      console.error('WebSocket error:', err)
      setError('WebSocket connection error')
    }

    return () => {
      ws.close()
    }
  }, [])

  const entries = markets.filter((m) => m.edge > 0)
  const rest = markets.filter((m) => !(m.edge > 0))

  return (
    <div className="animate-fade-in-up">
      <div className="page-header">
        <div>
          <div className="page-title">Overview</div>
          <div className="page-title-main">Market Scan</div>
        </div>
        <div className="page-actions">
          {lastUpdated && (
            <span className="font-mono" style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <span className="live-dot animate-pulse-dot" style={{ color: 'var(--green)', fontSize: '8px' }}>●</span>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', color: 'var(--red)', fontFamily: 'var(--font-mono)', fontSize: '11px', background: 'var(--red-muted)', border: '1px solid var(--red)', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading && markets.length === 0 && (
        <div className="empty-state">
          <span className="empty-state-text">
            Scanning markets
            <span className="animate-blink" style={{ color: 'var(--green)', marginLeft: '4px' }}>▋</span>
          </span>
        </div>
      )}

      {markets.length > 0 && (
        <>
          {entries.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div className="font-mono" style={{ fontSize: '9px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '10px' }}>
                ★ Snipes Found ({entries.length})
              </div>
              <div className="scan-grid">
                {entries.map((m, idx) => (
                  <ScanCard key={`${m.crypto}-${idx}`} market={m} />
                ))}
              </div>
            </div>
          )}

          {entries.length > 0 && rest.length > 0 && (
            <Separator className="my-4" />
          )}

          <div>
            <div className="font-mono" style={{ fontSize: '9px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '10px' }}>
              Other Markets ({rest.length})
            </div>
            {rest.length === 0 && !loading ? (
              <div style={{ padding: '20px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                No scan data yet
              </div>
            ) : (
              <div className="scan-grid">
                {rest.map((m, idx) => (
                  <ScanCard key={`${m.crypto}-${idx}`} market={m} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
